import { Box, useDisclosure, VStack } from "@chakra-ui/react";
import LoadingSpinnerOverlay from "components/LoadingSpinnerOverlay";
import { Roles, useIdentity } from "context/auth";
import { useInvalidateAccountSummary } from "hooks/useAccountSummary";
import useCancelPayoutRequest from "hooks/useCancelPayoutRequest";
import { useInvalidatePayoutRequests } from "hooks/usePayoutRequests";
import { ChaseACHPayoutMethodDetails, PayoutRequest, PayoutRequestMethod, PayoutRequestStatus } from "models/payoutRequests";
import { FC } from "react";

import { Modal } from "antd";
import usePayoutRequestInfoNeeded from "hooks/usePayoutRequestInfoNeeded";
import PayoutApproval from "./PayoutApproval";
import CancelConfirmationModal from "./RequestPayoutModal/CancelConfirmationModal";
import InfoNeededConfirmationModal from "./RequestPayoutModal/InfoNeededConfirmationModal";

const PayoutRequestActions: FC<{ request: PayoutRequest }> = ({ request }) => {
    const { id } = request;
    // The id is already baked in
    const { cancelPayoutRequest, isLoading: isCancelling } = useCancelPayoutRequest(id);
    const { payoutRequestInfoNeeded, isLoading: isRequestingInfo } = usePayoutRequestInfoNeeded(id);

    const invalidatePayoutRequests = useInvalidatePayoutRequests();
    const invalidateAccountSummary = useInvalidateAccountSummary();

    // Are you sure? cancel modal
    const {
        isOpen: cancelIsOpen,
        onOpen: cancelOnOpen,
        onClose: cancelOnClose
    } = useDisclosure();

    // Are you sure? info needed modal
    const {
        isOpen: infoIsOpen,
        onOpen: infoOnOpen,
        onClose: infoOnClose
    } = useDisclosure();

    const handleCancelClick = () => {
        cancelOnOpen();
    };

    const handleInfoClick = () => {
        infoOnOpen();
    }

    // Handle when the user confirms they want to cancel
    const handleCancel = async (reason: string) => {
        cancelOnClose();
        await cancelPayoutRequest(reason);

        // To update the balance
        invalidateAccountSummary();
        // To update the table
        invalidatePayoutRequests();
    };

    const handleInfoNeeded = async () => {
        infoOnClose();
        await payoutRequestInfoNeeded();
        invalidatePayoutRequests();
    }

    const systemCancelled = (reason: string) => {
        Modal.error({
            title: "Payout Request Cancelled",
            content: <>
                <Box>
                    Due an error submitting this payout request to an external system, it has been cancelled:
                </Box>
                <Box fontWeight='bold' mt={10}>
                    {reason}
                </Box>
            </>
        });
    }

    // Admins see the option to approve
    const { isAdmin, isInRole } = useIdentity();
    const isSeniorStaff = isInRole([Roles.SeniorStaff]);

    // ACH payouts no longer need  'Info Needed' w/ Chase integration
    const isAch = request.method === PayoutRequestMethod.ACH;
    const showInfoNeeded = isAdmin && !isAch && request.status !== PayoutRequestStatus.InfoNeeded;
    // non ACH payouts & ACH payouts over the configured threshold need approval,
    // but ACH payouts that do not meet the threshold cannot be canceled, as they
    // have already been sent to Chase
    const requiresApproval = (!isSeniorStaff && !isAch) || (isAch && (request.details as ChaseACHPayoutMethodDetails).requiresApproval);

    return (
        <>
            <LoadingSpinnerOverlay isLoading={isCancelling || isRequestingInfo} />
            <CancelConfirmationModal
                isAdmin={isAdmin}
                request={request}
                isOpen={cancelIsOpen}
                onClose={cancelOnClose}
                onConfirm={handleCancel}
            />
            <InfoNeededConfirmationModal
                request={request}
                isOpen={infoIsOpen}
                onClose={infoOnClose}
                onConfirm={handleInfoNeeded}
            />
            <VStack align="right" spacing={1}>
                {isAdmin && requiresApproval && <PayoutApproval request={request} onApproved={invalidatePayoutRequests} onCancelled={systemCancelled} />}
                {requiresApproval && <Box color="blue" as="a" onClick={handleCancelClick}>
                    Cancel
                </Box>}
                {showInfoNeeded && <Box color="blue" as="a" onClick={handleInfoClick}>
                    Info. Needed
                </Box>}
            </VStack>
        </>
    );
};

export default PayoutRequestActions;
